import { LegalLinkLine } from '@/components/legal/legal-document';
import { AnswerBox } from '@/components/seo/answer-box';
import {
  breadcrumbJsonLd,
  ChipLinks,
  FinalCta,
  LinkList,
  Reveal,
  SectionTitle,
  SeoSection,
} from '@/components/seo/seo-blocks';
import { SeoHero } from '@/components/seo/seo-hero';
import { SeoPageShell } from '@/components/seo/seo-page-shell';
import { SeoHead, SITE_URL } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import {
  ABOUT_ANSWER,
  ABOUT_DESCRIPTION,
  ABOUT_FACTS,
  ABOUT_PATH,
  ABOUT_SECTIONS,
  ABOUT_TITLE,
} from '@/content/legal/about';
import { SEGMENT_PAGES } from '@/content/seo/segments';
import { pageTitle } from '@/lib/seo/meta';
import { Href } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

/** Rótulos do breadcrumb: iguais na tela e no JSON-LD. */
const CRUMBS = [
  { label: 'Início', path: '/' },
  { label: 'Sobre', path: ABOUT_PATH },
];

/**
 * Página institucional (`/sobre`). É onde buscador e IA procuram "quem é essa
 * empresa"; o `AboutPage` aponta para a mesma `Organization` do `+html.tsx`.
 * Texto em `content/legal/about.ts`.
 */
export default function AboutScreen() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${SITE_URL}${ABOUT_PATH}#page`,
      url: `${SITE_URL}${ABOUT_PATH}`,
      name: ABOUT_TITLE,
      description: ABOUT_DESCRIPTION,
      inLanguage: 'pt-BR',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      mainEntity: { '@id': `${SITE_URL}/#organization` },
    },
    breadcrumbJsonLd(
      SITE_URL,
      CRUMBS.map((crumb) => ({ name: crumb.label, path: crumb.path })),
    ),
  ];

  return (
    <SeoPageShell
      screenTitle={ABOUT_TITLE}
      dockLabel="Organize seus clientes e pagamentos"
      head={
        <SeoHead
          title={pageTitle(ABOUT_TITLE)}
          description={ABOUT_DESCRIPTION}
          path={ABOUT_PATH}
          jsonLd={jsonLd}
        />
      }
    >
      <SeoHero
        width="narrow"
        crumbs={[{ label: CRUMBS[0].label, href: '/' }, { label: CRUMBS[1].label }]}
        title={ABOUT_TITLE}
      >
        <View className="mx-auto w-full max-w-4xl">
          <AnswerBox text={ABOUT_ANSWER} />
        </View>
      </SeoHero>

      {ABOUT_SECTIONS.map((section, index) => (
        <SeoSection key={section.id} narrow tone={index % 2 === 0 ? undefined : 'white'}>
          <SectionTitle title={section.question} />
          <Reveal className="gap-4">
            <ThemedText className="text-lg font-medium leading-relaxed text-neutral-800">
              {section.capsule}
            </ThemedText>
            {section.body.map((paragraph) => (
              <ThemedText key={paragraph} className="text-base leading-relaxed text-neutral-700">
                {paragraph}
              </ThemedText>
            ))}
            {section.id === 'para-quem' ? (
              <ChipLinks
                items={SEGMENT_PAGES.map((page) => ({
                  label: page.label,
                  href: `/para/${page.slug}` as Href,
                }))}
              />
            ) : null}
            {section.id === 'dinheiro' ? (
              <LinkList items={[{ label: 'Taxas e prazos do recebimento pelo link', href: '/ajuda/recebimentos' }]} />
            ) : null}
            {section.id === 'dados' ? (
              <LinkList items={[{ label: 'Política de privacidade', href: '/privacidade' }]} />
            ) : null}
          </Reveal>
        </SeoSection>
      ))}

      <SeoSection tone="white" narrow>
        <SectionTitle title="A Controlapag em resumo" />
        <View className="overflow-hidden rounded-2xl border border-neutral-900/5 bg-card">
          {ABOUT_FACTS.map((fact, index) => (
            <View
              key={fact.label}
              className={`flex-row items-center gap-4 px-5 py-4 ${index > 0 ? 'border-t border-neutral-900/5' : ''}`}
            >
              <ThemedText className="w-28 text-sm text-neutral-500">{fact.label}</ThemedText>
              <View className="flex-1">
                {fact.url ? (
                  <LegalLinkLine link={{ label: fact.value, url: fact.url }} />
                ) : (
                  <ThemedText className="font-medium text-sm text-neutral-800">{fact.value}</ThemedText>
                )}
              </View>
            </View>
          ))}
        </View>
        <View className="mt-8">
          <LinkList
            items={[
              { label: 'Fale com a Controlapag', href: '/contato' },
              { label: 'Central de suporte', href: '/suporte' },
            ]}
          />
        </View>
      </SeoSection>

      <FinalCta title="Cadastre seu primeiro cliente e veja como fica mais fácil" />
    </SeoPageShell>
  );
}
